import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { JRCCalendarEvent } from './JRCCalendarEvent';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import { JRCInputSelectRaw } from '../Form/Input/JRCSelect/JRCInputSelectRaw';
import { useIntl } from 'react-intl';
import { CalendarStyle } from './CalendarStyle';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import {
    CalendarEventViewAbstractType,
    CalendarEventWithExtensions,
    JArticleApi,
    JCalendarApi,
    JGlobalApi,
} from 'jamespot-user-api';
import { JRCLoader } from '../JRCLoader/JRCLoader';
import { FormatterInput } from '@fullcalendar/core';
import { noNull } from '../../utils/utils.array';
import { JRCFlexBox } from '../JRCFlex/JRCFlexBox';
import { JRCCalendarModal } from './JRCCalendarModal';
import { SubscriptionModifiers } from './JRCEventStatusButton';

export type JRCCalendarProps = {
    events?: CalendarEventWithExtensions<CalendarEventViewAbstractType, ['socialEventRecord']>[];
    loading?: boolean;
    onChangeDates?: (start: Date, end: Date) => void;
    onError: (errorMsg?: string) => void;
    onSuccess: (message: string) => void;
    handler: {
        global: Pick<JGlobalApi, 'updateAction'>;
        calendar: Pick<JCalendarApi, 'reviveSubmit'>;
        article: Pick<JArticleApi, 'delete'>;
    };
    defaultView: CalendarView;
    eventModalExtraComponent?: (
        event: CalendarEventWithExtensions<CalendarEventViewAbstractType, ['socialEventRecord']>,
    ) => ReactNode;
    onChangeView?: (view: CalendarView) => void;
};

export type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'list';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.md}px;
    flex: 1;
`;

const Toolbar = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${({ theme }) => theme.space.md}px;
`;

const Label = styled(JRCTypography)`
    text-transform: capitalize;
`;

const ArrowSelector = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.space.xs}px;
`;

const Today = styled.div``;

const ViewSelector = styled.div`
    max-width: 200px;
`;

export const JRCCalendar = ({
    events,
    loading,
    onChangeDates,
    onError,
    handler,
    onSuccess,
    defaultView,
    eventModalExtraComponent,
    onChangeView,
}: JRCCalendarProps) => {
    const intl = useIntl();
    const [label, setLabel] = useState<string>('');
    const [calendarWidth, setCalendarWidth] = useState<number>();
    const [displayedEvent, setDisplayedEvent] = useState<CalendarEventWithExtensions<
        CalendarEventViewAbstractType,
        ['socialEventRecord']
    > | null>(null);
    const calendarRef = useRef<FullCalendar>(null);
    const containerRef = useRef(null);
    const [subscriptionModifiers, setSubscriptionModifiers] = useState<SubscriptionModifiers>([]);

    const calendarAPI = useCallback(() => calendarRef.current?.getApi(), [calendarRef]);

    const listRange = useMemo(
        () => ({
            start: new Date().setHours(0, 0, 0, 0),
            end: new Date().setFullYear(2100),
        }),
        [],
    );

    const viewsOptions = useMemo<{ label: string; value: CalendarView }[]>(
        () => [
            {
                label: intl.formatMessage({ id: 'CALENDAR_dayGridMonth' }),
                value: 'dayGridMonth',
            },
            {
                label: intl.formatMessage({ id: 'CALENDAR_timeGridWeek' }),
                value: 'timeGridWeek',
            },
            {
                label: intl.formatMessage({ id: 'CALENDAR_timeGridDay' }),
                value: 'timeGridDay',
            },
            {
                label: intl.formatMessage({ id: 'CALENDAR_listWeek' }),
                value: 'list',
            },
        ],
        [intl],
    );

    const [view, setView] = React.useState<(typeof viewsOptions)[number]>(
        viewsOptions.find((v) => v.value === defaultView) ?? viewsOptions[0],
    );

    const doOnChangeView = useCallback(
        (value: (typeof viewsOptions)[number]) => {
            setView(value);
            onChangeView?.(value.value);
        },
        [onChangeView],
    );

    const fetchLabel = useCallback(() => {
        setLabel(calendarAPI()?.view.title ?? '');
    }, [calendarAPI]);

    useEffect(() => {
        calendarAPI()?.changeView(view.value, view.value === 'list' ? listRange : undefined);
        fetchLabel();
    }, [calendarAPI, fetchLabel, listRange, view]);

    calendarAPI()?.on('datesSet', () => {
        fetchLabel();
    });

    const locale = useMemo(() => {
        switch (intl.locale) {
            case 'fr':
                return frLocale;
            case 'es':
                return esLocale;
            default:
                return enLocale;
        }
    }, [intl.locale]);

    const formattedEvents = useMemo(() => {
        return events
            ?.map((event) => {
                let dateStart: Date;
                let dateEnd: Date | undefined = undefined;
                if (event.dateStart && event.dateEnd) {
                    dateStart = new Date(event.dateStart);
                    dateEnd = new Date(event.dateEnd);
                } else if (event.dateStart) {
                    dateStart = new Date(event.dateStart);
                } else if (event.dateEnd) {
                    dateStart = new Date(event.dateEnd);
                } else {
                    console.error(`Event ${event.uri} has no dateStart or dateEnd defined.`);
                    return null;
                }

                return {
                    id: String(event.id),
                    title: event.title,
                    start: dateStart,
                    end: dateEnd,
                    status: event._extend?.socialEventRecord?.userResponseStatus ?? null,
                    audience: event._audience,
                    allDay: event.allDay,
                    bgColor: event.backgroundColor,
                    txtColor: event.textColor,
                    address: event.address,
                    hasImage: !!event.image,
                    type: event.type,
                    isVisio: event._extend?.socialEventRecord?.isVisio,
                    noGestion: event.noGestion,
                    uri: event.uri,
                    canSubscribe:
                        !event.socialEventUseCeiling ||
                        event.socialEventCeiling === null ||
                        event._extend?.socialEventRecord === undefined ||
                        event._extend.socialEventRecord === null ||
                        event._extend.socialEventRecord.subscribersTotal +
                            (subscriptionModifiers.find((s) => s.uri === event.uri)?.modifier ?? 0) <
                            (event.socialEventCeiling ?? -1),
                };
            })
            .filter(noNull);
    }, [events, subscriptionModifiers]);

    const dayHeaderFormat: FormatterInput | undefined = useMemo(() => {
        switch (view.value) {
            case 'dayGridMonth':
                if (calendarWidth && calendarWidth > 780) {
                    return { weekday: 'long' };
                } else {
                    return { weekday: 'short' };
                }
            case 'timeGridWeek':
                if (calendarWidth && calendarWidth > 1200) {
                    return { weekday: 'long', day: '2-digit', month: '2-digit' };
                } else if (calendarWidth && calendarWidth > 750) {
                    return { weekday: 'short', day: '2-digit', month: '2-digit' };
                } else if (calendarWidth && calendarWidth > 650) {
                    return { weekday: 'narrow', day: '2-digit', month: '2-digit' };
                } else {
                    return { day: 'numeric', month: '2-digit' };
                }
            default:
                return undefined;
        }
    }, [calendarWidth, view.value]);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            setCalendarWidth(entries[0].contentRect.width);
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <>
            <Container>
                <Toolbar>
                    {loading && (
                        <div>
                            <JRCLoader size={'s'} />
                        </div>
                    )}
                    {view.value !== 'list' && (
                        <>
                            <Label size={'l'} weight={'medium'}>
                                {label}
                            </Label>
                            <ArrowSelector>
                                <JRCIconButton
                                    icon={'icon-chevron-left'}
                                    onClick={() => calendarAPI()?.prev()}
                                    color={'grey5'}
                                />
                                <JRCIconButton
                                    icon={'icon-chevron-right'}
                                    onClick={() => calendarAPI()?.next()}
                                    color={'grey5'}
                                />
                            </ArrowSelector>
                            <Today>
                                <JRCIconButton
                                    icon={'icon-calendar'}
                                    onClick={() => calendarAPI()?.today()}
                                    tooltip={{ description: 'CALENDAR_Today', position: 'top' }}
                                    color={'grey5'}
                                />
                            </Today>
                        </>
                    )}
                    <ViewSelector>
                        <JRCInputSelectRaw
                            onChange={doOnChangeView}
                            value={view}
                            name={'selectView'}
                            options={viewsOptions}
                            getLabel={(option) => option.label}
                            getValue={(option) => option.value}
                            required
                            minWidth={false}
                        />
                    </ViewSelector>
                </Toolbar>
                <JRCFlexBox x={'center'} flex={'1'}>
                    <CalendarStyle loading={loading} ref={containerRef} view={view.value === 'list' ? 'list' : 'grid'}>
                        <FullCalendar
                            dayHeaderFormat={dayHeaderFormat}
                            datesSet={onChangeDates ? (d) => onChangeDates(d.start, d.end) : undefined}
                            headerToolbar={false}
                            firstDay={1}
                            locale={locale}
                            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
                            initialView={defaultView}
                            events={formattedEvents}
                            eventContent={(eventContent) => (
                                <JRCCalendarEvent
                                    key={new Date().getTime()}
                                    id={eventContent.event.id}
                                    status={eventContent.event.extendedProps.status}
                                    title={eventContent.event.title}
                                    start={eventContent.event.start}
                                    allDay={eventContent.event.allDay}
                                    end={eventContent.event.end}
                                    display={view.value}
                                    audience={eventContent.event.extendedProps.audience}
                                    backgroundColor={eventContent.event.extendedProps.bgColor}
                                    textColor={eventContent.event.extendedProps.txtColor}
                                    address={eventContent.event.extendedProps.address}
                                    image={eventContent.event.extendedProps.hasImage}
                                    type={eventContent.event.extendedProps.type}
                                    isVisio={eventContent.event.extendedProps.isVisio}
                                    noGestion={eventContent.event.extendedProps.noGestion}
                                    onError={onError}
                                    handler={handler}
                                    onSuccess={onSuccess}
                                    setSubscriptionModifiers={setSubscriptionModifiers}
                                    onClick={() =>
                                        setDisplayedEvent(
                                            (prev) =>
                                                events?.find(
                                                    (e) =>
                                                        e.id === Number(eventContent.event.id) &&
                                                        e.type === eventContent.event.extendedProps.type &&
                                                        e.uri !== prev?.uri,
                                                ) ?? null,
                                        )
                                    }
                                    uri={eventContent.event.extendedProps.uri}
                                    canSubscribe={eventContent.event.extendedProps.canSubscribe}
                                    subscriptionModifiers={subscriptionModifiers}
                                />
                            )}
                            ref={calendarRef}
                            allDayText={''}
                            allDaySlot={events?.some((e) => e.allDay)}
                            height={'100%'}
                            dayMaxEvents={4}
                            eventMaxStack={view.value === 'timeGridWeek' ? 2 : 4}
                        />
                    </CalendarStyle>
                </JRCFlexBox>
            </Container>
            <JRCCalendarModal
                event={displayedEvent ?? undefined}
                open={displayedEvent !== null}
                closeHandler={() => setDisplayedEvent(null)}
                handler={handler}
                onError={onError}
                onSuccess={onSuccess}
                extraComponent={eventModalExtraComponent}
                setSubscriptionModifiers={setSubscriptionModifiers}
                subscriptionModifiers={subscriptionModifiers}
            />
        </>
    );
};
